/**
 * LineDetailPage - Detailansicht/Neuanlage einer Strecke
 * 
 * Create-Mode (/admin/lines/new):
 * - Name eingeben
 * - Optional: Übergeordnete Strecke auswählen
 *   - Keine Auswahl → Hauptstrecke wird angelegt
 *   - Auswahl → Sub-Strecke der gewählten Hauptstrecke wird angelegt
 * 
 * Edit-Mode (/admin/lines/:id):
 * - Nur für Hauptstrecken (Sub-Strecken haben eigene SubLineDetailPage)
 * - Grunddaten bearbeiten
 * - Sub-Strecken inline hinzufügen
 * - Gefahrenzone für Deaktivierung
 * 
 * Analog zum Kunden/Kundengruppen-Pattern
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Route, ArrowLeft, RefreshCw, Save, Trash2, AlertTriangle, Plus, X, ChevronRight, Check, Info, Settings, RotateCcw } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

interface SubLine {
  id: string;
  name: string;
  isActive: boolean;
}

interface Line {
  id: string;
  name: string;
  isActive: boolean;
  subLines: SubLine[];
}

interface ArticleGroupEntity {
  id: string;
  code: string;
  name: string;
}

interface LaundryClassEntity {
  id: string;
  code: string;
  name: string;
  // NEU: Pro-LC Artikelgruppen-Regeln (von API)
  requiresArticleGroup?: boolean;
  allowedArticleGroups?: ArticleGroupEntity[];
}

interface CaptureConfig {
  id: string;
  screen: 'FL' | 'WSA';
  lineId: string | null;
  lineName: string | null;
  requiresLaundryClass: boolean;
  requiresArticleGroup: boolean;
  allowedLaundryClasses: LaundryClassEntity[];
  allowedArticleGroups: ArticleGroupEntity[];
}

// Alphabetische Sortierung
const sortAlpha = <T extends { name: string }>(items: T[]): T[] => {
  return [...items].sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }));
};

export default function LineDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';
  
  const [line, setLine] = useState<Line | null>(null);
  const [allLines, setAllLines] = useState<Line[]>([]); // Für Dropdown
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form State
  const [name, setName] = useState('');
  const [parentLineId, setParentLineId] = useState<string>(''); // Leer = Hauptstrecke
  
  // Delete Confirm State
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Inline SubLine Create State (nur Edit-Mode)
  const [showSubLineForm, setShowSubLineForm] = useState(false);
  const [subLineName, setSubLineName] = useState('');
  const [subLineSaving, setSubLineSaving] = useState(false);
  const [subLineError, setSubLineError] = useState<string | null>(null);
  const subLineInputRef = useRef<HTMLInputElement>(null);

  // Erfassungs-Flow State (CaptureConfig)
  const [lineConfig, setLineConfig] = useState<CaptureConfig | null>(null);
  const [globalConfig, setGlobalConfig] = useState<CaptureConfig | null>(null);
  const [allLaundryClasses, setAllLaundryClasses] = useState<LaundryClassEntity[]>([]);
  const [allArticleGroups, setAllArticleGroups] = useState<ArticleGroupEntity[]>([]);
  const [captureConfigLoading, setCaptureConfigLoading] = useState(false);
  const [captureConfigSaving, setCaptureConfigSaving] = useState(false);
  const [captureConfigError, setCaptureConfigError] = useState<string | null>(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  
  // Erfassungs-Flow Edit State - Wäscheklasse
  const [flowRequiresLaundryClass, setFlowRequiresLaundryClass] = useState(false);
  const [flowAllowedClassIds, setFlowAllowedClassIds] = useState<string[]>([]);
  const [flowAllowAll, setFlowAllowAll] = useState(true);
  
  // Erfassungs-Flow Edit State - Artikelgruppe
  const [flowRequiresArticleGroup, setFlowRequiresArticleGroup] = useState(false);
  const [flowAllowedGroupIds, setFlowAllowedGroupIds] = useState<string[]>([]);
  const [flowAllowAllGroups, setFlowAllowAllGroups] = useState(true);
  
  // Pro-LC Einstellungen State - EXPLIZITE MODI
  // mode: 'none' = Keine Artikelgruppe | 'all' = Alle erlaubt | 'whitelist' = Nur bestimmte
  type ArticleGroupMode = 'none' | 'all' | 'whitelist';
  const [perLcSettings, setPerLcSettings] = useState<Record<string, { mode: ArticleGroupMode; allowedArticleGroupIds: string[] }>>({}); 
  const [expandedLcId, setExpandedLcId] = useState<string | null>(null);
  
  // Globaler Fallback-Modus (statt flowRequiresArticleGroup + flowAllowAllGroups)
  const [globalArticleGroupMode, setGlobalArticleGroupMode] = useState<ArticleGroupMode>('none');

  // Load all lines for dropdown (Create-Mode)
  const loadAllLines = async () => {
    try {
      const res = await fetch('/api/admin/lines', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAllLines(data.filter((l: Line) => l.isActive !== false));
      }
    } catch (err) {
      console.error('Fehler beim Laden der Strecken:', err);
    }
  };

  // Load CaptureConfig for this line (Edit-Mode)
  const loadCaptureConfig = async () => {
    if (isNew || !id) return;
    setCaptureConfigLoading(true);
    setCaptureConfigError(null);
    try {
      // Load all configs to find lineConfig and globalConfig
      const res = await fetch('/api/admin/capture-configs', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const configs: CaptureConfig[] = data.configs || [];
        
        // Find line-specific config for FL
        const lc = configs.find(c => c.screen === 'FL' && c.lineId === id) || null;
        setLineConfig(lc);
        
        // Find global config for FL
        const gc = configs.find(c => c.screen === 'FL' && c.lineId === null) || null;
        setGlobalConfig(gc);
        
        // Initialize edit state from lineConfig (or defaults)
        if (lc) {
          setFlowRequiresLaundryClass(lc.requiresLaundryClass);
          setFlowRequiresArticleGroup(lc.requiresArticleGroup);
          setFlowAllowedClassIds(lc.allowedLaundryClasses.map(c => c.id));
          setFlowAllowAll(lc.allowedLaundryClasses.length === 0);
          setFlowAllowedGroupIds(lc.allowedArticleGroups.map(g => g.id));
          setFlowAllowAllGroups(lc.allowedArticleGroups.length === 0);
          
          // Pro-LC Settings aus API laden (mit expliziten Modi)
          const perLc: Record<string, { mode: 'none' | 'all' | 'whitelist'; allowedArticleGroupIds: string[] }> = {};
          lc.allowedLaundryClasses.forEach(lcItem => {
            if (lcItem.requiresArticleGroup !== undefined) {
              const agIds = (lcItem.allowedArticleGroups || []).map(ag => ag.id);
              let mode: 'none' | 'all' | 'whitelist';
              if (!lcItem.requiresArticleGroup) {
                mode = 'none';
              } else if (agIds.length === 0) {
                mode = 'all';
              } else {
                mode = 'whitelist';
              }
              perLc[lcItem.id] = { mode, allowedArticleGroupIds: agIds };
            }
          });
          setPerLcSettings(perLc);
          
          // Globalen Modus aus API ableiten
          if (!lc.requiresArticleGroup) {
            setGlobalArticleGroupMode('none');
          } else if (lc.allowedArticleGroups.length === 0) {
            setGlobalArticleGroupMode('all');
          } else {
            setGlobalArticleGroupMode('whitelist');
          }
        } else {
          // Reset to defaults when no lineConfig
          setFlowRequiresLaundryClass(false);
          setFlowRequiresArticleGroup(false);
          setFlowAllowedClassIds([]);
          setFlowAllowAll(true);
          setFlowAllowedGroupIds([]);
          setFlowAllowAllGroups(true);
          setPerLcSettings({}); // Reset pro-LC settings
        }
      }
    } catch (err) {
      console.error('Fehler beim Laden der CaptureConfig:', err);
    }
    setCaptureConfigLoading(false);
  };

  // Load all LaundryClasses
  const loadLaundryClasses = async () => {
    try {
      const res = await fetch('/api/data/laundry-classes', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const classes = Array.isArray(data) ? data : (data.laundryClasses || []);
        setAllLaundryClasses(classes.filter((lc: LaundryClassEntity & { isActive?: boolean }) => lc.isActive !== false));
      }
    } catch (err) {
      console.error('Fehler beim Laden der Wäscheklassen:', err);
    }
  };

  // Load all ArticleGroups
  const loadArticleGroups = async () => {
    try {
      const res = await fetch('/api/data/article-groups', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const groups = Array.isArray(data) ? data : (data.articleGroups || []);
        setAllArticleGroups(groups.filter((ag: ArticleGroupEntity & { isActive?: boolean }) => ag.isActive !== false));
      }
    } catch (err) {
      console.error('Fehler beim Laden der Artikelgruppen:', err);
    }
  };

  // Load line details (Edit-Mode)
  const loadLine = async () => {
    if (isNew || !id) return;
    try {
      const res = await fetch(`/api/admin/lines/${id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLine(data);
        setName(data.name);
      } else if (res.status === 404) {
        navigate('/admin/lines');
      }
    } catch (err) {
      console.error('Fehler beim Laden der Strecke:', err);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      if (isNew) {
        await loadAllLines();
      } else {
        await Promise.all([
          loadLine(),
          loadAllLines(),
          loadCaptureConfig(),
          loadLaundryClasses(),
          loadArticleGroups(),
        ]);
      }
      setLoading(false);
    };
    init();
  }, [id, isNew]);

  // Save
  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name ist erforderlich');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (isNew) {
        if (parentLineId) {
          // Sub-Strecke anlegen
          const res = await fetch(`/api/admin/lines/${parentLineId}/sublines`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ name: name.trim() }),
          });

          if (res.ok) {
            const newSubLine = await res.json();
            navigate(`/admin/sublines/${newSubLine.id}`);
          } else {
            const data = await res.json().catch(() => ({}));
            setError(data.error || data.message || 'Fehler beim Anlegen');
          }
        } else {
          // Hauptstrecke anlegen
          const res = await fetch('/api/admin/lines', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ name: name.trim() }),
          });

          if (res.ok) {
            const newLine = await res.json();
            navigate(`/admin/lines/${newLine.id}`);
          } else {
            const data = await res.json().catch(() => ({}));
            setError(data.error || data.message || 'Fehler beim Anlegen');
          }
        }
      } else {
        // Edit-Mode: Hauptstrecke aktualisieren
        const res = await fetch(`/api/admin/lines/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: name.trim() }),
        });

        if (res.ok) {
          navigate('/admin/lines');
        } else {
          const data = await res.json().catch(() => ({}));
          setError(data.error || data.message || 'Fehler beim Speichern');
        }
      }
    } catch (err) {
      setError('Netzwerkfehler');
    }
    setSaving(false);
  };

  // Delete (soft delete)
  const handleDelete = async () => {
    if (!id || isNew) return;
    
    setDeleting(true);
    try {
      const res = await fetch(`/api/admin/lines/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (res.ok) {
        navigate('/admin/lines');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Fehler beim Deaktivieren');
        setDeleteConfirmOpen(false);
      }
    } catch (err) {
      console.error('Fehler beim Deaktivieren:', err);
    }
    setDeleting(false);
  };

  // Check if line can be deleted (no active sublines)
  const canDelete = (): boolean => {
    if (!line) return false;
    const activeSubLines = line.subLines?.filter(sl => sl.isActive !== false) || [];
    return activeSubLines.length === 0;
  };

  // Aktive Sub-Strecken alphabetisch sortiert
  const activeSubLines = sortAlpha(line?.subLines?.filter(sl => sl.isActive !== false) || []);

  // Erfassungs-Flow: Effektive Config (lineConfig > globalConfig > default)
  const effectiveConfig = lineConfig || globalConfig || null;
  const configSource: 'line' | 'global' | 'default' = lineConfig ? 'line' : (globalConfig ? 'global' : 'default');

  // Erfassungs-Flow: Create/Update lineConfig
  const handleSaveCaptureConfig = async () => {
    if (!id || isNew) return;
    setCaptureConfigSaving(true);
    setCaptureConfigError(null);

    try {
      // Pro-LC Einstellungen für API vorbereiten (explizite Modi → Backend-Format)
      const perLcSettingsPayload = Object.entries(perLcSettings)
        .map(([lcId, s]) => ({
          laundryClassId: lcId,
          requiresArticleGroup: s.mode !== 'none', // none = false, all/whitelist = true
          allowedArticleGroupIds: s.mode === 'whitelist' ? s.allowedArticleGroupIds : [],
        }));

      // Globalen Modus in Backend-Format umwandeln
      const globalRequiresAG = globalArticleGroupMode !== 'none';
      const globalAllowedAGIds = globalArticleGroupMode === 'whitelist' ? flowAllowedGroupIds : [];

      const payload = {
        screen: 'FL' as const,
        lineId: id,
        requiresLaundryClass: flowRequiresLaundryClass,
        requiresArticleGroup: globalRequiresAG,
        allowedLaundryClassIds: flowRequiresLaundryClass && !flowAllowAll ? flowAllowedClassIds : [],
        allowedArticleGroupIds: globalAllowedAGIds,
        perLcSettings: perLcSettingsPayload,
      };

      if (lineConfig) {
        // Update existing
        const res = await fetch(`/api/admin/capture-configs/${lineConfig.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            requiresLaundryClass: payload.requiresLaundryClass,
            requiresArticleGroup: payload.requiresArticleGroup,
            allowedLaundryClassIds: payload.allowedLaundryClassIds,
            allowedArticleGroupIds: payload.allowedArticleGroupIds,
            perLcSettings: payload.perLcSettings, // NEU
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Fehler beim Speichern');
        }
      } else {
        // Create new
        const res = await fetch('/api/admin/capture-configs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Fehler beim Anlegen');
        }
      }

      await loadCaptureConfig();
    } catch (err: any) {
      setCaptureConfigError(err.message || 'Unbekannter Fehler');
    }
    setCaptureConfigSaving(false);
  };

  // Erfassungs-Flow: Delete lineConfig (reset to global)
  const handleResetCaptureConfig = async () => {
    if (!lineConfig) return;
    setCaptureConfigSaving(true);
    setCaptureConfigError(null);

    try {
      const res = await fetch(`/api/admin/capture-configs/${lineConfig.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Fehler beim Zurücksetzen');
      }
      setResetConfirmOpen(false);
      await loadCaptureConfig();
    } catch (err: any) {
      setCaptureConfigError(err.message || 'Unbekannter Fehler');
    }
    setCaptureConfigSaving(false);
  };

  // Checkbox für erlaubte Wäscheklasse togglen
  const toggleAllowedClass = (classId: string) => {
    setFlowAllowedClassIds(prev =>
      prev.includes(classId)
        ? prev.filter(id => id !== classId)
        : [...prev, classId]
    );
  };

  // Inline SubLine handlers (Edit-Mode)
  const openSubLineForm = () => {
    setSubLineName('');
    setSubLineError(null);
    setShowSubLineForm(true);
    setTimeout(() => subLineInputRef.current?.focus(), 0);
  };

  const cancelSubLineForm = () => {
    setShowSubLineForm(false);
    setSubLineName('');
    setSubLineError(null);
  };

  const handleCreateSubLine = async () => {
    const trimmedName = subLineName.trim();
    if (!trimmedName) {
      setSubLineError('Name ist erforderlich');
      return;
    }

    setSubLineSaving(true);
    setSubLineError(null);

    try {
      const res = await fetch(`/api/admin/lines/${id}/sublines`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: trimmedName, sortOrder: 0 }),
      });

      if (res.ok) {
        setSubLineName('');
        await loadLine();
        setTimeout(() => subLineInputRef.current?.focus(), 0);
      } else {
        const data = await res.json().catch(() => ({}));
        setSubLineError(data.error || data.message || 'Fehler beim Anlegen');
      }
    } catch (err) {
      setSubLineError('Netzwerkfehler');
    }
    setSubLineSaving(false);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Laden...</div>
      </AdminLayout>
    );
  }

  if (!isNew && !line) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Strecke nicht gefunden</div>
      </AdminLayout>
    );
  }

  // Sortierte Hauptstrecken für Dropdown
  const sortedLines = sortAlpha(allLines);

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link
            to="/admin/lines"
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Route className="w-6 h-6" />
              {isNew ? 'Neue Strecke' : line?.name}
            </h1>
            {!isNew && line && (
              <p className="text-sm text-slate-500 mt-0.5">
                Hauptstrecke
                {!line.isActive && <span className="ml-2 text-red-500">(inaktiv)</span>}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {!isNew && (
              <button
                onClick={() => loadLine()}
                disabled={loading}
                className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                title="Aktualisieren"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || !name.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Grunddaten */}
        <section className="bg-white rounded-lg border p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 border-b pb-2">Grunddaten</h2>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Mangel, Trockner, Finisher"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
          </div>

          {/* Dropdown für übergeordnete Strecke (nur Create-Mode) */}
          {isNew && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Übergeordnete Strecke
              </label>
              <div className="relative">
                <Route className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <select
                  value={parentLineId}
                  onChange={(e) => setParentLineId(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Keine (Hauptstrecke anlegen)</option>
                  {sortedLines.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {parentLineId 
                  ? 'Wird als Sub-Strecke der gewählten Hauptstrecke angelegt'
                  : 'Wird als neue Hauptstrecke angelegt'
                }
              </p>
            </div>
          )}
        </section>

        {/* Sub-Strecken (nur Edit-Mode) */}
        {!isNew && (
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h2 className="text-lg font-semibold text-slate-900">Sub-Strecken</h2>
              {!showSubLineForm && (
                <button
                  onClick={openSubLineForm}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Hinzufügen
                </button>
              )}
            </div>

            {/* Inline Create Row */}
            {showSubLineForm && (
              <div className="space-y-2">
                {subLineError && (
                  <div className="p-2 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
                    {subLineError}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input
                    ref={subLineInputRef}
                    type="text"
                    value={subLineName}
                    onChange={(e) => setSubLineName(e.target.value)}
                    placeholder="Name der Sub-Strecke..."
                    className="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && subLineName.trim()) {
                        e.preventDefault();
                        handleCreateSubLine();
                      } else if (e.key === 'Escape') {
                        cancelSubLineForm();
                      }
                    }}
                    disabled={subLineSaving}
                  />
                  <button
                    onClick={handleCreateSubLine}
                    disabled={subLineSaving || !subLineName.trim()}
                    className="p-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    title="Hinzufügen (Enter)"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={cancelSubLineForm}
                    className="p-1.5 border border-slate-300 text-slate-500 rounded-lg hover:bg-slate-50"
                    title="Schließen (Escape)"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* SubLines List */}
            {activeSubLines.length === 0 && !showSubLineForm ? (
              <p className="text-sm text-slate-500">Keine Sub-Strecken vorhanden</p>
            ) : activeSubLines.length > 0 ? (
              <ul className="divide-y -mx-2">
                {activeSubLines.map(sl => (
                  <li key={sl.id}>
                    <Link
                      to={`/admin/sublines/${sl.id}`}
                      className="flex items-center justify-between py-2 px-2 hover:bg-slate-50 rounded group"
                    >
                      <span className="font-medium text-sm">{sl.name}</span>
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600" />
                    </Link>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        )}

        {/* Erfassungsregeln Sektion (nur Edit-Mode) */}
        {!isNew && (
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Erfassungsregeln
              </h2>
              {lineConfig && (
                <button
                  onClick={() => setResetConfirmOpen(true)}
                  className="text-sm text-slate-500 hover:text-red-600 flex items-center gap-1"
                  title="Auf globale Einstellung zurücksetzen"
                >
                  <RotateCcw className="w-4 h-4" />
                  Zurücksetzen
                </button>
              )}
            </div>

            {/* Hinweistext */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              Hier legen Sie fest, welche Angaben der Vorarbeiter bei der Erfassung für diese Strecke machen muss.
            </div>

            {/* Status-Anzeige */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-700">Status:</span>
              {configSource === 'line' && (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-sm rounded-full">
                  Strecken-spezifisch
                </span>
              )}
              {configSource === 'global' && (
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-sm rounded-full">
                  Globale Regeln aktiv
                </span>
              )}
              {configSource === 'default' && (
                <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-sm rounded-full">
                  Keine Regeln definiert
                </span>
              )}
            </div>

            {captureConfigError && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {captureConfigError}
              </div>
            )}

            {captureConfigLoading ? (
              <div className="text-sm text-slate-500">Laden...</div>
            ) : configSource === 'line' ? (
              /* Edit-Mode: lineConfig bearbeiten */
              <div className="space-y-6">
                {/* ══════════════════════════════════════════════════════════════ */}
                {/* SECTION 1: Wäscheklasse */}
                {/* ══════════════════════════════════════════════════════════════ */}
                <div className="space-y-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={flowRequiresLaundryClass}
                      onChange={(e) => setFlowRequiresLaundryClass(e.target.checked)}
                      className="w-5 h-5 mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="font-medium text-slate-900">Wäscheklasse muss erfasst werden</span>
                      <p className="text-sm text-slate-500">Der Vorarbeiter muss eine Wäscheklasse auswählen</p>
                    </div>
                  </label>

                  {flowRequiresLaundryClass && (
                    <div className="ml-8 pl-4 border-l-2 border-slate-200 space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="allowMode"
                          checked={flowAllowAll}
                          onChange={() => setFlowAllowAll(true)}
                          className="w-4 h-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-slate-700">Alle Wäscheklassen zulässig</span>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="allowMode"
                          checked={!flowAllowAll}
                          onChange={() => setFlowAllowAll(false)}
                          className="w-4 h-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-slate-700">Nur folgende Wäscheklassen zulässig:</span>
                      </label>

                      {!flowAllowAll && (
                        <div className="ml-7 space-y-2">
                          {allLaundryClasses.length === 0 ? (
                            <p className="text-sm text-slate-500">Keine Wäscheklassen verfügbar</p>
                          ) : (
                            allLaundryClasses.map(lc => (
                              <label key={lc.id} className="flex items-center gap-3 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={flowAllowedClassIds.includes(lc.id)}
                                  onChange={() => toggleAllowedClass(lc.id)}
                                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-slate-700">{lc.name}</span>
                              </label>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* ══════════════════════════════════════════════════════════════ */}
                {/* SECTION 2: Standardregel für Artikelgruppen (Fallback) */}
                {/* ══════════════════════════════════════════════════════════════ */}
                <div className="pt-4 border-t border-slate-200 space-y-4">
                  <div>
                    <h4 className="font-medium text-slate-900">Standardregel für Artikelgruppen (Fallback)</h4>
                    <p className="text-xs text-slate-500">Pro Wäscheklasse kann diese Regel überschrieben werden.</p>
                  </div>

                  <div className="ml-4 space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="globalArticleGroupMode"
                        checked={globalArticleGroupMode === 'none'}
                        onChange={() => setGlobalArticleGroupMode('none')}
                        className="w-4 h-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-700">🟢 Keine Artikelgruppe erfassen</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="globalArticleGroupMode"
                        checked={globalArticleGroupMode === 'all'}
                        onChange={() => setGlobalArticleGroupMode('all')}
                        className="w-4 h-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-700">🔵 Alle Artikelgruppen erlaubt</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="globalArticleGroupMode"
                        checked={globalArticleGroupMode === 'whitelist'}
                        onChange={() => setGlobalArticleGroupMode('whitelist')}
                        className="w-4 h-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-700">🟠 Nur bestimmte Artikelgruppen erlauben</span>
                    </label>

                    {globalArticleGroupMode === 'whitelist' && (
                      <div className="ml-7 space-y-1 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2 bg-white">
                        {allArticleGroups.length === 0 ? (
                          <p className="text-sm text-slate-500">Keine Artikelgruppen verfügbar</p>
                        ) : (
                          sortAlpha(allArticleGroups).map((ag) => (
                            <label key={ag.id} className="flex items-center gap-2 cursor-pointer py-1">
                              <input
                                type="checkbox"
                                checked={flowAllowedGroupIds.includes(ag.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFlowAllowedGroupIds([...flowAllowedGroupIds, ag.id]);
                                  } else {
                                    setFlowAllowedGroupIds(flowAllowedGroupIds.filter(gid => gid !== ag.id));
                                  }
                                }}
                                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-slate-700">{ag.name}</span>
                            </label>
                          ))
                        )}
                        {flowAllowedGroupIds.length === 0 && (
                          <p className="text-xs text-red-500 mt-2">Mindestens eine Artikelgruppe auswählen</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* ══════════════════════════════════════════════════════════════ */}
                {/* SECTION 3: Pro-Wäscheklasse Artikelgruppen-Regeln */}
                {/* ══════════════════════════════════════════════════════════════ */}
                {flowRequiresLaundryClass && allLaundryClasses.length > 0 && (
                  <div className="pt-4 border-t border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-slate-900">Pro-Wäscheklasse Artikelgruppen-Regeln</h4>
                        <p className="text-xs text-slate-500">Überschreibt die Standardregel für einzelne Wäscheklassen</p>
                      </div>
                      {/* Badge: Anzahl mit eigener Regel */}
                      {(() => {
                        const count = Object.keys(perLcSettings).length;
                        return count > 0 ? (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                            {count} mit eigener Regel
                          </span>
                        ) : null;
                      })()}
                    </div>

                    <div className="space-y-2">
                      {/* Wäscheklassen-Liste (je nach Modus) */}
                      {(flowAllowAll ? allLaundryClasses : allLaundryClasses.filter(lc => flowAllowedClassIds.includes(lc.id))).map(lc => {
                        const isExpanded = expandedLcId === lc.id;
                        const lcSettings = perLcSettings[lc.id];
                        const hasOwnRule = !!lcSettings;
                        
                        // Modus-Anzeige
                        const getModeDisplay = () => {
                          if (!lcSettings) return { icon: '⚪', text: 'Standard', color: 'text-slate-500' };
                          switch (lcSettings.mode) {
                            case 'none': return { icon: '🟢', text: 'Keine AG', color: 'text-green-700' };
                            case 'all': return { icon: '🔵', text: 'Alle AG', color: 'text-blue-700' };
                            case 'whitelist': return { icon: '🟠', text: `${lcSettings.allowedArticleGroupIds.length} AG`, color: 'text-amber-700' };
                          }
                        };
                        const modeDisplay = getModeDisplay();
                        
                        return (
                          <div key={lc.id} className={`border rounded bg-white ${hasOwnRule ? 'border-blue-300 bg-blue-50/30' : 'border-slate-200'}`}>
                            <button
                              type="button"
                              onClick={() => setExpandedLcId(isExpanded ? null : lc.id)}
                              className="w-full flex items-center justify-between p-3 text-left hover:bg-slate-50"
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-sm text-slate-900 font-medium">{lc.name}</span>
                                <span className={`text-xs px-2 py-0.5 rounded ${hasOwnRule ? 'bg-blue-100' : 'bg-slate-100'} ${modeDisplay.color}`}>
                                  {modeDisplay.icon} {modeDisplay.text}
                                </span>
                              </div>
                              <span className="text-xs text-blue-600">{isExpanded ? '▲' : '▼'}</span>
                            </button>
                            
                            {isExpanded && (
                              <div className="px-4 pb-4 pt-2 border-t border-slate-100 bg-slate-50">
                                <p className="text-xs text-slate-600 mb-3">
                                  <strong>Artikelgruppen-Regel für „{lc.name}":</strong>
                                </p>
                                
                                <div className="space-y-2">
                                  <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                      type="radio"
                                      name={`lcMode_${lc.id}`}
                                      checked={!lcSettings}
                                      onChange={() => {
                                        // Eigene Regel entfernen → Standard gilt
                                        setPerLcSettings(prev => {
                                          const next = { ...prev };
                                          delete next[lc.id];
                                          return next;
                                        });
                                      }}
                                      className="w-4 h-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-slate-700">⚪ Standard verwenden ({globalArticleGroupMode === 'none' ? 'Keine AG' : globalArticleGroupMode === 'all' ? 'Alle AG' : 'Whitelist'})</span>
                                  </label>
                                  
                                  <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                      type="radio"
                                      name={`lcMode_${lc.id}`}
                                      checked={lcSettings?.mode === 'none'}
                                      onChange={() => setPerLcSettings(prev => ({ ...prev, [lc.id]: { mode: 'none', allowedArticleGroupIds: [] } }))}
                                      className="w-4 h-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-slate-700">🟢 Keine Artikelgruppe erfassen</span>
                                  </label>
                                  
                                  <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                      type="radio"
                                      name={`lcMode_${lc.id}`}
                                      checked={lcSettings?.mode === 'all'}
                                      onChange={() => setPerLcSettings(prev => ({ ...prev, [lc.id]: { mode: 'all', allowedArticleGroupIds: [] } }))}
                                      className="w-4 h-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-slate-700">🔵 Alle Artikelgruppen erlaubt</span>
                                  </label>
                                  
                                  <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                      type="radio"
                                      name={`lcMode_${lc.id}`}
                                      checked={lcSettings?.mode === 'whitelist'}
                                      onChange={() => setPerLcSettings(prev => ({ ...prev, [lc.id]: { mode: 'whitelist', allowedArticleGroupIds: prev[lc.id]?.allowedArticleGroupIds || [] } }))}
                                      className="w-4 h-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-slate-700">🟠 Nur bestimmte Artikelgruppen erlauben</span>
                                  </label>
                                  
                                  {lcSettings?.mode === 'whitelist' && (
                                    <div className="ml-7 mt-2 space-y-1 max-h-32 overflow-y-auto border border-slate-200 rounded-lg p-2 bg-white">
                                      {allArticleGroups.map(ag => (
                                        <label key={ag.id} className="flex items-center gap-2 cursor-pointer py-1">
                                          <input
                                            type="checkbox"
                                            checked={lcSettings.allowedArticleGroupIds.includes(ag.id)}
                                            onChange={(e) => {
                                              setPerLcSettings(prev => ({
                                                ...prev,
                                                [lc.id]: {
                                                  ...prev[lc.id],
                                                  allowedArticleGroupIds: e.target.checked
                                                    ? [...prev[lc.id].allowedArticleGroupIds, ag.id]
                                                    : prev[lc.id].allowedArticleGroupIds.filter(x => x !== ag.id)
                                                }
                                              }));
                                            }}
                                            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                          />
                                          <span className="text-sm text-slate-700">{ag.name}</span>
                                        </label>
                                      ))}
                                      {lcSettings.allowedArticleGroupIds.length === 0 && (
                                        <p className="text-xs text-red-500">Mindestens eine auswählen</p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {!flowAllowAll && flowAllowedClassIds.length === 0 && (
                        <p className="text-sm text-slate-500 text-center py-4 bg-slate-50 rounded-lg border border-slate-200">
                          Wählen Sie oben mindestens eine Wäscheklasse aus
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Speichern Button */}
                <div className="pt-4 border-t border-slate-200">
                  <button
                    onClick={handleSaveCaptureConfig}
                    disabled={captureConfigSaving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {captureConfigSaving ? 'Speichern...' : 'Regeln speichern'}
                  </button>
                </div>
              </div>
            ) : (
              /* Read-Only Mode: global/default anzeigen */
              <div className="space-y-4">
                {effectiveConfig ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-600">Wäscheklasse erforderlich:</span>
                      <span className={effectiveConfig.requiresLaundryClass ? 'text-green-600 font-medium' : 'text-slate-500'}>
                        {effectiveConfig.requiresLaundryClass ? 'Ja' : 'Nein'}
                      </span>
                    </div>
                    {effectiveConfig.requiresLaundryClass && (
                      <div className="flex items-start gap-2">
                        <span className="text-slate-600">Zulässige Klassen:</span>
                        <span className="text-slate-700">
                          {effectiveConfig.allowedLaundryClasses.length === 0
                            ? 'Alle'
                            : effectiveConfig.allowedLaundryClasses.map(c => c.name).join(', ')
                          }
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-slate-600">Artikelgruppe erforderlich:</span>
                      <span className={effectiveConfig.requiresArticleGroup ? 'text-green-600 font-medium' : 'text-slate-500'}>
                        {effectiveConfig.requiresArticleGroup ? 'Ja' : 'Nein'}
                      </span>
                    </div>
                    {effectiveConfig.requiresArticleGroup && (
                      <div className="flex items-start gap-2">
                        <span className="text-slate-600">Zulässige Gruppen:</span>
                        <span className="text-slate-700">
                          {effectiveConfig.allowedArticleGroups.length === 0
                            ? 'Alle'
                            : effectiveConfig.allowedArticleGroups.map(g => g.name).join(', ')
                          }
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">
                    Keine Regeln definiert.
                  </p>
                )}

                {/* Button: Strecken-spezifische Regeln erstellen */}
                <button
                  onClick={() => {
                    // Initialize edit state from global config or defaults
                    if (globalConfig) {
                      setFlowRequiresLaundryClass(globalConfig.requiresLaundryClass);
                      setFlowRequiresArticleGroup(globalConfig.requiresArticleGroup);
                      setFlowAllowedClassIds(globalConfig.allowedLaundryClasses.map(c => c.id));
                      setFlowAllowAll(globalConfig.allowedLaundryClasses.length === 0);
                      setFlowAllowedGroupIds(globalConfig.allowedArticleGroups.map(g => g.id));
                      setFlowAllowAllGroups(globalConfig.allowedArticleGroups.length === 0);
                    } else {
                      setFlowRequiresLaundryClass(false);
                      setFlowRequiresArticleGroup(false);
                      setFlowAllowedClassIds([]);
                      setFlowAllowAll(true);
                      setFlowAllowedGroupIds([]);
                      setFlowAllowAllGroups(true);
                    }
                    handleSaveCaptureConfig();
                  }}
                  disabled={captureConfigSaving}
                  className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {captureConfigSaving ? 'Erstellen...' : 'Strecken-spezifische Regeln erstellen'}
                </button>
              </div>
            )}
          </section>
        )}

        {/* Hinweis */}
        {!isNew && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Hinweis zu historischen Daten</p>
              <p className="mt-1">
                Änderungen am Streckenstamm wirken sich nicht auf bereits erfasste Daten aus.
                Diese enthalten Snapshot-Daten zum Zeitpunkt der Erfassung.
              </p>
            </div>
          </div>
        )}

        {/* Delete Section (nur bei Edit) */}
        {!isNew && line && (
          <section className="bg-white rounded-lg border border-red-200 p-6">
            <h2 className="font-semibold text-red-600 mb-2">Gefahrenzone</h2>
            {!canDelete() ? (
              <div className="flex items-start gap-3 text-amber-700 bg-amber-50 p-3 rounded-lg">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <strong>Deaktivieren nicht möglich:</strong> Diese Hauptstrecke hat noch {activeSubLines.length} aktive Sub-Strecke(n). 
                  Bitte entfernen Sie zuerst alle Sub-Strecken.
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-600 mb-4">
                  Das Deaktivieren einer Hauptstrecke entfernt sie aus der Auswahl. 
                  Bereits erfasste Daten bleiben unverändert.
                </p>
                <button
                  onClick={() => setDeleteConfirmOpen(true)}
                  className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Hauptstrecke deaktivieren
                </button>
              </>
            )}
          </section>
        )}
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-4 border-b flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-lg font-semibold text-red-600">Hauptstrecke deaktivieren?</h2>
            </div>
            <div className="p-4">
              <p className="text-slate-600">
                <strong>"{line?.name}"</strong> wird deaktiviert.
              </p>
              <p className="text-sm text-slate-500 mt-2">
                Die Strecke kann nicht mehr für neue Erfassungen verwendet werden.
              </p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deaktivieren...' : 'Deaktivieren'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset CaptureConfig Confirm Modal */}
      {resetConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-4 border-b flex items-center gap-3">
              <RotateCcw className="w-6 h-6 text-amber-600" />
              <h2 className="text-lg font-semibold text-amber-700">Auf globale Einstellung zurücksetzen?</h2>
            </div>
            <div className="p-4">
              <p className="text-slate-600">
                Die streckenspezifische Erfassungs-Flow Einstellung wird gelöscht.
              </p>
              <p className="text-sm text-slate-500 mt-2">
                {globalConfig 
                  ? 'Es gilt dann die globale Einstellung.'
                  : 'Es gilt dann der Standard (keine Wäscheklasse-Abfrage).'
                }
              </p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
              <button
                onClick={() => setResetConfirmOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Abbrechen
              </button>
              <button
                onClick={handleResetCaptureConfig}
                disabled={captureConfigSaving}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {captureConfigSaving ? 'Zurücksetzen...' : 'Zurücksetzen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
